import { useEffect, useMemo, useState } from "react";
import { RangeType, useAsu } from "@framework/provider-asu";
import { SortDirection } from "@framework/types-collection";

export const useTrainTables = <TSortColumn>(params: {
  isActiveTableTab?: boolean;
  defaultSortColumn: TSortColumn;
  onReset: () => void;
  onSort?: () => void;
}) => {
  const { isActiveTableTab, defaultSortColumn, onReset, onSort } = params;

  const {
    selectedItem,
    startTimestamp,
    endTimestamp,
    dbTz,
    userSelectedRangeType,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
    recorderWindowStart,
    recorderTickId,
  } = useAsu();

  const isRecorderMode = userSelectedRangeType === RangeType.RECORDER_MODE;
  const trainId = (selectedItem && "TrainId" in selectedItem && selectedItem.TrainId) || null;

  const [sortColumn, setSortColumn] = useState<TSortColumn>(defaultSortColumn);
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.desc);

  useEffect(() => {
    setSortColumn(defaultSortColumn);
    setSortOrder(SortDirection.desc);
    onReset();
  }, [
    trainId,
    startTimestamp,
    endTimestamp,
    isActiveTableTab,
    isRecorderMode,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
  ]);

  const canFetch = useMemo(() => {
    if (!trainId) {
      return false;
    }
    if (isActiveTableTab === false) {
      return false;
    }
    if (isRecorderMode && !recorderTickId) {
      return false;
    }
    return true;
  }, [trainId, isActiveTableTab, isRecorderMode, recorderTickId]);

  const handleSort = (value: TSortColumn) => () => {
    if (isRecorderMode) return;
    setSortColumn(value);
    setSortOrder(prev => (prev === SortDirection.asc ? SortDirection.desc : SortDirection.asc));
    onSort?.();
  };

  return {
    selectedItem,
    trainId,
    startTimestamp,
    endTimestamp,
    dbTz,
    isRecorderMode,
    recorderWindowStart,
    recorderTickId,
    sortColumn,
    sortOrder,
    handleSort,
    canFetch,
  };
};
